import type { SupabaseClient } from '@supabase/supabase-js'
import type { User, UserRole } from '@/types/auth'
import { coerceUserRoles, primaryRoleFromRoles } from '@/lib/auth/roles'

interface ProfileRow {
  id: string
  name: string
  email: string
  roles: unknown
  patient_id: string | null
}

export type ProfileLookupResult =
  | { status: 'found'; user: User }
  | { status: 'missing' }
  | { status: 'error'; error: unknown }

export type ProfileRecoveryResult =
  | { status: 'resolved'; user: User }
  | { status: 'error'; error: unknown }

function toUser(row: ProfileRow): User {
  const roles = coerceUserRoles(row.roles)
  return {
    id: row.id,
    email: row.email,
    name: row.name,
    roles,
    role: primaryRoleFromRoles(roles),
    patientId: row.patient_id ?? undefined,
  }
}

export async function fetchProfileForAuth(
  supabase: SupabaseClient,
  userId: string
): Promise<ProfileLookupResult> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name, email, roles, patient_id')
    .eq('id', userId)
    .maybeSingle()

  if (error) {
    return { status: 'error', error }
  }

  if (!data) {
    return { status: 'missing' }
  }

  return { status: 'found', user: toUser(data as ProfileRow) }
}

export async function ensureProfileAndFetch(
  supabase: SupabaseClient,
  userId: string,
  email: string,
  name?: string
): Promise<ProfileRecoveryResult> {
  const existing = await fetchProfileForAuth(supabase, userId)
  if (existing.status === 'found') {
    return { status: 'resolved', user: existing.user }
  }

  if (existing.status === 'error') {
    return existing
  }

  const { error: upsertError } = await supabase.from('profiles').upsert(
    {
      id: userId,
      email: email ?? '',
      name: name ?? email ?? '',
      roles: ['Patient'],
    },
    {
      onConflict: 'id',
      ignoreDuplicates: true,
    }
  )

  if (upsertError) {
    return { status: 'error', error: upsertError }
  }

  const afterUpsert = await fetchProfileForAuth(supabase, userId)
  if (afterUpsert.status === 'found') {
    return { status: 'resolved', user: afterUpsert.user }
  }

  if (afterUpsert.status === 'error') {
    return afterUpsert
  }

  return {
    status: 'error',
    error: new Error(`Profile bootstrap completed but profile ${userId} is still missing`),
  }
}
